// ============================================
// pages/EditProject.tsx
// 作者编辑自己的作品. URL: /p/:shortId/edit
//   - 预填现有值, 调 community-edit-project
//   - 改标题/简介/教程会触发后端重新审核(提示用户)
//   - 积木/代码不可改(要回 Studio 重新分享)
// ============================================

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import CoverUploader from '../components/CoverUploader';
import { callApi } from '../services/api';
import { getCachedUser } from '../services/auth';
import { loadCategories, type Category } from '../services/categories';
import type { ProjectPublic } from '../types/community';

export default function EditProject() {
  const { shortId } = useParams<{ shortId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tutorial, setTutorial] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shortId) return;
    (async () => {
      const r = await callApi<{ project: ProjectPublic }>('community-get-project', { short_id: shortId });
      if (!r.ok || !r.project) { setError('作品不存在'); setLoading(false); return; }
      const p = r.project as ProjectPublic;
      // 作者本人校验
      const me = getCachedUser();
      if (!me || me.user_id !== p.author_user_id) { setForbidden(true); setLoading(false); return; }

      setTitle(p.title);
      setDescription(p.description);
      setCoverUrl(p.cover_url || '');
      setCategory(p.category);
      setTags(p.tags || []);
      setTutorial(p.tutorial_md || '');
      setCategories(await loadCategories());
      setLoading(false);
    })();
  }, [shortId]);

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, '');
    if (!t || tags.includes(t) || tags.length >= 5) { setTagInput(''); return; }
    setTags([...tags, t]); setTagInput('');
  };

  const onSave = async () => {
    setError(null);
    if (!title.trim()) { setError('请填写标题'); return; }
    if (description.trim().length < 20) { setError('简介至少 20 个字'); return; }
    setSubmitting(true);
    const r = await callApi<{ re_audit: boolean; message: string }>('community-edit-project', {
      short_id: shortId,
      title: title.trim(),
      description: description.trim(),
      cover_url: coverUrl.trim() || null,
      category,
      tags,
      tutorial_md: tutorial.trim(),
    });
    setSubmitting(false);
    if (!r.ok) { setError(r.message || r.error || '保存失败'); return; }
    // 改了关键内容会重审,提示用户
    if (r.re_audit) {
      alert('已保存。由于修改了标题或内容,作品需要重新审核通过后才会再次公开展示。');
    }
    navigate(`/p/${shortId}`, { replace: true });
  };

  if (loading) return (
    <Layout width="narrow" showSearch={false}>
      <div className="py-24 flex justify-center">
        <LoadingSpinner hint="加载作品…" />
      </div>
    </Layout>
  );

  if (forbidden) {
    return (
      <Layout width="narrow">
        <div className="py-20 text-center text-ink-500">只能编辑自己的作品。</div>
      </Layout>
    );
  }

  return (
    <Layout width="narrow" showSearch={false}>
      <div className="py-8 max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-ink-900 mb-1">编辑作品</h1>
        <p className="text-ink-500 text-sm mb-6">
          积木和代码不能在这里修改;如需更新程序,请回到 HiPiiDo Studio 重新分享。修改标题或简介后,作品会重新进入审核。
        </p>

        <div className="bg-white border border-ink-300/40 rounded-2xl p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1.5">标题 *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2.5 border border-ink-300/60 rounded-xl text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1.5">简介 *(至少 20 字)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
              className="w-full px-3 py-2.5 border border-ink-300/60 rounded-xl text-sm resize-y" />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1.5">封面图</label>
            <CoverUploader shortId={shortId || ''} value={coverUrl} onChange={setCoverUrl} />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1.5">分类</label>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button key={c.id} onClick={() => setCategory(c.id)}
                  className={`px-4 py-1.5 rounded-pill text-sm border transition-colors ${
                    category === c.id ? 'bg-gold-500 text-white border-gold-500' : 'bg-white text-ink-600 border-ink-300/60'
                  }`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1.5">标签(最多 5 个)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 bg-paper rounded-pill text-xs text-ink-600">
                  #{t}
                  <button onClick={() => setTags(tags.filter((x) => x !== t))} className="text-ink-400 hover:text-red-500">×</button>
                </span>
              ))}
            </div>
            <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              placeholder="输入标签后回车" className="w-full px-3 py-2.5 border border-ink-300/60 rounded-xl text-sm" />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button onClick={onSave} disabled={submitting} className="btn-primary flex-1">
              {submitting ? '保存中…' : '保存修改'}
            </button>
            <button onClick={() => navigate(`/p/${shortId}`, { replace: true })} className="btn-secondary px-6">取消</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
